import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { Role } from '../../../../../models/Role';
import { User } from '../../../../../models/User';
import { EffectiveUserRoleAssignment, UserRoleAssignment } from '../../../../../models/UserRoleAssignment';
import { PermissionsService } from '../../../../../services/permissions.service';
import { Logger } from '../../../../../../../infrastructure/logging/logger';

@Component({
  selector: 'app-user-role-mapping',
  templateUrl: './user-role-mapping.component.html',
  styleUrls: ['./user-role-mapping.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinner
  ]
})
export class UserRoleMappingComponent implements OnInit {

  loading = new BehaviorSubject<boolean>(false);
  saving = new BehaviorSubject<boolean>(false);
  users: User[] = [];
  roles: Role[] = [];
  assignments: UserRoleAssignment[] = [];
  departmentSourcedKeys = new Set<string>();
  searchText = '';

  get filteredUsers(): User[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return this.users;
    }

    return this.users.filter(user =>
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  }

  constructor(
    private permissionsService: PermissionsService,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.loadPage();
  }

  async loadPage() {
    try {
      this.loading.next(true);

      const [users, roles, rows] = await Promise.all([
        this.permissionsService.getCloudUsers(),
        this.permissionsService.getRoles(),
        this.permissionsService.getUserRoleAssignments()
      ]);

      this.users = users;
      this.roles = roles;

      const effectiveRows = rows.filter(row => row.userId && row.roleId);

      this.departmentSourcedKeys = new Set(
        effectiveRows
          .filter(row => row.source === 'department')
          .map(row => this.assignmentKey(row.userId, row.roleId))
      );

      this.assignments = this.groupByUser(
        effectiveRows.filter(row => row.source !== 'department')
      );
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving User Role Mappings', 'Error while retrieving User Role Mappings');
    } finally {
      this.loading.next(false);
    }
  }

  private assignmentKey(userId: string, roleId: string): string {
    return `${userId}|${roleId}`;
  }

  private groupByUser(rows: EffectiveUserRoleAssignment[]): UserRoleAssignment[] {
    const byUserId = new Map<string, UserRoleAssignment>();

    for (const row of rows) {
      const existing = byUserId.get(row.userId);

      if (!existing) {
        byUserId.set(row.userId, { id: '', userId: row.userId, roleIds: [row.roleId] });
      } else if (!existing.roleIds.includes(row.roleId)) {
        existing.roleIds.push(row.roleId);
      }
    }

    return Array.from(byUserId.values());
  }

  trackByUserId(_: number, user: User): string {
    return user.userId;
  }

  isFromDepartment(user: User, roleId: string): boolean {
    return this.departmentSourcedKeys.has(this.assignmentKey(user.userId, roleId));
  }

  isChecked(user: User, roleId: string): boolean {
    if (this.isFromDepartment(user, roleId)) {
      return true;
    }

    const assignment = this.assignments.find(a => a.userId === user.userId);
    return !!assignment?.roleIds.includes(roleId);
  }

  isAllSelected(user: User): boolean {
    return this.roles.length > 0 && this.roles.every(role => this.isChecked(user, role.id));
  }

  isIndeterminate(user: User): boolean {
    const checkedCount = this.roles.filter(role => this.isChecked(user, role.id)).length;
    return checkedCount > 0 && checkedCount < this.roles.length;
  }

  onToggle(user: User, roleId: string): void {
    if (this.isFromDepartment(user, roleId)) {
      return;
    }

    const assignment = this.assignments.find(a => a.userId === user.userId);

    if (!assignment) {
      this.assignments.push({ id: '', userId: user.userId, roleIds: [roleId] });
      return;
    }

    const index = assignment.roleIds.indexOf(roleId);
    if (index >= 0) {
      assignment.roleIds.splice(index, 1);
    } else {
      assignment.roleIds.push(roleId);
    }
  }

  onToggleSelectAll(user: User): void {
    const selectAll = !this.isAllSelected(user);

    this.roles
      .filter(role => !this.isFromDepartment(user, role.id))
      .forEach(role => this.setChecked(user, role.id, selectAll));
  }

  private setChecked(user: User, roleId: string, checked: boolean): void {
    const assignment = this.assignments.find(a => a.userId === user.userId);

    if (!assignment) {
      if (checked) {
        this.assignments.push({ id: '', userId: user.userId, roleIds: [roleId] });
      }
      return;
    }

    const index = assignment.roleIds.indexOf(roleId);
    if (checked && index < 0) {
      assignment.roleIds.push(roleId);
    } else if (!checked && index >= 0) {
      assignment.roleIds.splice(index, 1);
    }
  }

  async onSaveClick(): Promise<void> {
    try {
      this.saving.next(true);
      this.loading.next(true);
      this.assignments = await this.permissionsService.saveUserRoleAssignments(this.assignments);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while saving User Role Mappings', 'Error while saving User Role Mappings');
    } finally {
      this.loading.next(false);
      this.saving.next(false);
    }
  }
}
